import {
  type AuditLog,
  isDefined,
  isNotDefined,
  isUnassignedTeamId,
  isUnassignedUser,
  SECOND,
  type TeamId,
  type UserId,
} from '@terros/common'
import { type TeamChange, type UserChange, type UserChangeType } from './ownershipChanges'

/** Who the account was given to when it was created or imported */
export type CreationAssignment = {
  ownerId?: UserId
  closerId?: UserId
  teamId?: TeamId
}

type OwnershipChange = UserChange | TeamChange

type IsCreationChangeProps = {
  change: OwnershipChange
  creation: AuditLog | undefined
}

/**
 * A first owner, closer or team (nothing before it) dated no later than the creating save was set by that save,
 * so it belongs in "created / imported the account" rather than its own line.
 */
export const isCreationChange = ({ change, creation }: IsCreationChangeProps): boolean => {
  if (isNotDefined(creation)) return false
  if (change.type === 'team' && isDefined(change.previousTeamId)) return false
  if (change.type !== 'team' && isDefined(change.previousUserId)) return false
  return change.timestamp <= creation.timestamp + SECOND
}

type ToCreationAssignmentProps = {
  changes: OwnershipChange[]
  creation: AuditLog
}

export const toCreationAssignment = ({ changes, creation }: ToCreationAssignmentProps): CreationAssignment => {
  const ownerId = findAssignedUserId({ changes, type: 'owner' })
  return {
    // "Erik created the account" already says the creator owns it
    ownerId: ownerId === creation.userId ? undefined : ownerId,
    closerId: findAssignedUserId({ changes, type: 'closer' }),
    teamId: findAssignedTeamId(changes),
  }
}

type FindAssignedUserIdProps = {
  changes: OwnershipChange[]
  type: UserChangeType
}

const findAssignedUserId = ({ changes, type }: FindAssignedUserIdProps): UserId | undefined => {
  const change = changes.find((item) => item.type === type)
  if (isNotDefined(change) || change.type === 'team' || isUnassignedUser(change.userId)) return
  return change.userId
}

const findAssignedTeamId = (changes: OwnershipChange[]): TeamId | undefined => {
  const change = changes.find((item) => item.type === 'team')
  if (isNotDefined(change) || change.type !== 'team' || isUnassignedTeamId(change.teamId)) return
  return change.teamId
}
